import React from 'react';
import {
  FaServer,
  FaExclamationTriangle,
  FaExclamationCircle,
  FaTools,
} from 'react-icons/fa';
import { Card, Button, Spinner, EmptyState } from './Primitives';

/** Scheletro di caricamento iniziale della dashboard. */
export function LoadingState() {
  return (
    <Card className="p-10">
      <div className="flex flex-col items-center justify-center gap-3 text-center">
        <Spinner className="h-8 w-8" />
        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
          Caricamento inventario ESXi…
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500">
          Recupero stato host e macchine virtuali.
        </p>
      </div>
    </Card>
  );
}

/**
 * Banner d'errore classificato: host non configurato, irraggiungibile o errore
 * generico. Nessun dettaglio tecnico interno viene mostrato.
 */
export function ErrorState({ error, onRetry, retrying }) {
  const kind = error?.kind || 'error';
  const view = {
    not_configured: {
      icon: FaTools,
      title: 'Host ESXi non configurato',
      hint: 'La connessione all’host di test non è ancora stata configurata sul server. Contatta l’amministratore di sistema.',
      accent: 'text-gray-400',
      showRetry: false,
    },
    unreachable: {
      icon: FaExclamationTriangle,
      title: 'Host ESXi non raggiungibile',
      hint: 'L’host di test non risponde al momento. I dati mostrati, se presenti, potrebbero non essere aggiornati.',
      accent: 'text-amber-500',
      showRetry: true,
    },
    error: {
      icon: FaExclamationCircle,
      title: 'Errore nel caricamento dei dati',
      hint: error?.message || 'Si è verificato un errore imprevisto durante il recupero dei dati ESXi.',
      accent: 'text-red-500',
      showRetry: true,
    },
  }[kind];

  const Icon = view.icon;
  return (
    <Card className="p-10">
      <div className="flex flex-col items-center justify-center gap-3 text-center">
        <Icon className={view.accent} size={32} aria-hidden="true" />
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
          {view.title}
        </h3>
        <p className="max-w-md text-sm text-gray-500 dark:text-gray-400">{view.hint}</p>
        {view.showRetry && onRetry && (
          <Button variant="primary" onClick={onRetry} disabled={retrying} className="mt-1">
            Riprova
          </Button>
        )}
      </div>
    </Card>
  );
}

/** Inventario vuoto (nessuna VM registrata o nessun risultato dopo i filtri). */
export function EmptyInventory({ filtered, onResetFilters }) {
  return (
    <Card className="p-8">
      <EmptyState
        icon={FaServer}
        title={filtered ? 'Nessuna VM corrisponde ai filtri' : 'Nessuna VM registrata'}
        hint={
          filtered
            ? 'Prova a modificare o azzerare i filtri per vedere altre macchine virtuali.'
            : 'L’host non ha macchine virtuali registrate, oppure l’inventario è momentaneamente vuoto.'
        }
        action={
          filtered && onResetFilters ? (
            <Button variant="secondary" onClick={onResetFilters} className="mt-2">
              Azzera filtri
            </Button>
          ) : null
        }
      />
    </Card>
  );
}
